import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { lessonApi } from '@/services/learningManagementApi';
import { usePaginatedList, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Eye, Edit, Trash2, BookOpen, Search } from 'lucide-react';
import { LoadingState, EmptyState, Pagination } from '@/components/common';
import { formatDate } from '@/utils/dateFormatter';
import type { Lesson, LessonStatus } from '@/types/learningManagement';

export const LessonListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const { hasPermission } = useAdminAuth();

  const canCreate = hasPermission('create', 'lesson');
  const canEdit = hasPermission('edit', 'lesson');
  const canDelete = hasPermission('delete', 'lesson');

  // Build fetch function with filters
  const fetchLessonsFn = useMemo(() => {
    return async (params: { page: number; size: number }) => {
      if (searchQuery.trim()) {
        return lessonApi.search({
          query: searchQuery,
          page: params.page,
          size: params.size,
        });
      } else {
        return lessonApi.list({
          page: params.page,
          size: params.size,
          sortBy: 'createdAt',
          sortDirection: 'desc',
        });
      }
    };
  }, [searchQuery]);

  // Use paginated list hook
  const {
    data: lessons,
    isLoading,
    error,
    pagination,
    setPage,
    setSize,
    refetch,
  } = usePaginatedList<Lesson>({
    fetchFn: fetchLessonsFn,
    entityName: 'lessons',
    suppress404Errors: true,
  });

  // Apply client-side filters (only for display, pagination still uses API data)
  const filteredLessons = useMemo(() => {
    if (!lessons) return [];
    let filtered = lessons;
    if (filterCategory !== 'all') {
      filtered = filtered.filter((l) => l.category === filterCategory);
    }
    if (filterStatus !== 'all') {
      filtered = filtered.filter((l) => l.status === filterStatus);
    }
    return filtered;
  }, [lessons, filterCategory, filterStatus]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (lessonId: string) => {
      await lessonApi.delete(lessonId);
    },
    entityName: 'lesson',
    operation: 'delete',
    onSuccess: () => {
      refetch();
    },
  });

  const handleDelete = async (lessonId: string) => {
    if (!window.confirm('Are you sure you want to delete this lesson?')) return;
    await deleteMutation.mutate(lessonId);
  };

  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  const getStatusColor = (status?: LessonStatus) => {
    if (!status) return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'DRAFT':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'PAUSED':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'ARCHIVED':
      case 'EXPIRED':
      case 'COMPLETED':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const lessonId = (lesson: Lesson) => lesson.id || '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Lesson Management</h1>
          <p className="text-muted-foreground mt-2">Manage all lessons</p>
        </div>
        {canCreate && (
          <Button asChild>
            <Link to="/admin/lessons/new">
              <Plus className="mr-2 h-4 w-4" />
              Create Lesson
            </Link>
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="md:col-span-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Search lessons..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Select value={filterCategory} onValueChange={(value) => {
              setFilterCategory(value);
              setPage(0);
            }}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="REMINDER">Reminder</SelectItem>
                <SelectItem value="MOTIVATION">Motivation</SelectItem>
                <SelectItem value="HABIT_BUILDING">Habit Building</SelectItem>
                <SelectItem value="PROGRESS_CHECK">Progress Check</SelectItem>
                <SelectItem value="GOAL_ALIGNMENT">Goal Alignment</SelectItem>
                <SelectItem value="SKILL_DEVELOPMENT">Skill Development</SelectItem>
                <SelectItem value="LEARNING_PATH">Learning Path</SelectItem>
                <SelectItem value="ASSESSMENT_PREP">Assessment Prep</SelectItem>
                <SelectItem value="REFLECTION">Reflection</SelectItem>
                <SelectItem value="CELEBRATION">Celebration</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={(value) => {
              setFilterStatus(value);
              setPage(0);
            }}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="PAUSED">Paused</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="EXPIRED">Expired</SelectItem>
                <SelectItem value="ARCHIVED">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Lessons Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lessons ({filteredLessons.length}{filterCategory !== 'all' || filterStatus !== 'all' ? ` of ${pagination.totalElements}` : ''})</CardTitle>
          <CardDescription>List of all lessons</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading lessons..." />
          ) : (error || lessons.length === 0) ? (
            <EmptyState
              title="No data found"
              description={error ? "Unable to load lessons. Please try again or create a new one." : "Get started by creating your first lesson."}
              icon={BookOpen}
              actions={[
                ...(canCreate ? [{
                  label: 'Create a new one',
                  onClick: () => navigate('/admin/lessons/new'),
                  variant: 'default' as const,
                }] : []),
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline' as const,
                },
              ]}
              searchQuery={searchQuery.trim() || undefined}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLessons.length === 0 && (filterCategory !== 'all' || filterStatus !== 'all') ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        No lessons match the selected filters. Try adjusting your filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLessons.map((lesson) => (
                    <TableRow key={lessonId(lesson)}>
                      <TableCell className="font-medium">{lesson.title}</TableCell>
                      <TableCell>{lesson.category}</TableCell>
                      <TableCell>{lesson.lessonType}</TableCell>
                      <TableCell>{lesson.priority}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(lesson.status)}`}>
                          {lesson.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        {formatDate(lesson.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/lessons/${lessonId(lesson)}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" asChild>
                              <Link to={`/admin/lessons/${lessonId(lesson)}/edit`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(lessonId(lesson))}
                              disabled={deleteMutation.isLoading}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                totalElements={pagination.totalElements}
                size={pagination.size}
                onPageChange={setPage}
                onSizeChange={setSize}
                isLoading={isLoading}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LessonListPage;






